/**
 * The product pages — `/status/chatgpt`, `/status/codex`, `/status/claude`, `/status/gemini` — and
 * the rule that decides whether each one is a page at all.
 *
 * People search for products, not companies: "chatgpt down" outnumbers "openai down" and "claude
 * down" outnumbers "anthropic down" (`docs/seo/keywords.md`). So the products get pages of their
 * own. What stops that being four thin copies of three provider pages is the gate below, and it is
 * evaluated against the live component list rather than assumed once when the registry was
 * written: a product page exists only while it has numbers of its own.
 *
 * It earns its keep immediately. Google publishes three components — Gemini, Gemini Notebook,
 * Gemini on Agent Platform — so `/status/gemini`'s filter matches all three, its page would be
 * `/status/google` under a different heading, and the route redirects instead. The Gemini query is
 * still answered: the Google page's own h2 asks it.
 *
 * The registry itself is not written here. `SEO_PRODUCTS` is generated from `docs/seo/products.json`
 * into `seo/copy.ts`, and `MustHaveAi.Web.Public.Seo.ProductPages` is the C# half reading the same
 * file — the filter, the gate and the narrowing below are deliberately line-for-line that class, so
 * a cold load and a client-side navigation cannot disagree about which pages exist.
 */

import type {
  ComponentState,
  ProviderComponent,
  ProviderState,
  ProviderStatus,
} from '@/net/statusApi'
import { isProviderId, type ProviderId } from '@/net/transport'
import { PROVIDERS } from '@/providers'
import { SEO_COPY, SEO_PRODUCTS, type SeoProduct } from '@/seo/copy'
import type { Crumb } from '@/ui/Breadcrumbs'

/** One product slice of a provider's status page, with its parent narrowed to an id this build tracks. */
export interface Product {
  /** The URL segment: `/status/{slug}`. */
  readonly slug: string
  /** The product in prose — the subject of every sentence on its page. */
  readonly label: string
  readonly parent: ProviderId
  /** How the parent's components are filtered down to this product's own. */
  readonly match: SeoProduct['match']
}

/**
 * The four, in the generated order.
 *
 * A row whose parent is not one of the three providers this build knows is dropped rather than
 * rendered against a provider that does not exist. The copy generator validates the same thing on
 * the way in, so this can only fire on a bundle built against a newer `products.json`.
 */
export const PRODUCTS: readonly Product[] = SEO_PRODUCTS.flatMap((product) =>
  isProviderId(product.parent)
    ? [
        {
          slug: product.slug,
          label: product.label,
          parent: product.parent,
          match: product.match,
        },
      ]
    : [],
)

/** The product a `/status/:provider` segment names, or null when it names none. */
export function findProduct(slug: string | undefined): Product | null {
  if (slug === undefined) return null
  return PRODUCTS.find((product) => product.slug === slug) ?? null
}

/**
 * Whether one of the parent's components belongs to this product.
 *
 * Case-insensitive on all three kinds, matching `ProductPages.Matches`: the provider's own
 * capitalisation of a group is theirs to change, and a filter that silently selected nothing after
 * they lower-cased a heading would turn a page into a redirect without anybody noticing.
 */
export function matchesProduct(product: Product, component: ProviderComponent): boolean {
  const { kind, values } = product.match
  return values.some((value) => {
    const needle = value.toLowerCase()
    if (kind === 'group') return (component.group ?? '').toLowerCase() === needle
    if (kind === 'name') return component.name.toLowerCase() === needle
    return component.name.toLowerCase().startsWith(needle)
  })
}

/** What a request for a product URL should do, once the parent's components are known. */
export type ProductOutcome = 'page' | 'redirect'

/**
 * The gate: a page only when the filter selects some of the parent's components but not all of
 * them.
 *
 * Zero matches is a product the provider has stopped publishing; every match is a product that
 * *is* the provider. Both would put the parent's numbers under a second name, and duplicate pages
 * do not rank twice, they rank half.
 *
 * The server answers 301 for the redirect case (`SpaShellMiddleware`), so a cold load never
 * reaches React at all. This exists for the navigation that does not touch the server: a menu link
 * to `/status/gemini` must land on the same page there as it does in a fresh tab.
 */
export function productOutcome(
  product: Product,
  components: readonly ProviderComponent[],
): ProductOutcome {
  if (components.length === 0) return 'redirect'
  const matched = components.filter((component) => matchesProduct(product, component)).length
  return matched > 0 && matched < components.length ? 'page' : 'redirect'
}

/** Where a product that failed the gate sends the reader — the parent, anchored at the product. */
export function productRedirect(product: Product): string {
  return `/status/${product.parent}#${product.slug}`
}

/** Component states worst-first, so a product's own state is the worst of the parts it owns. */
const SEVERITY: Record<ComponentState, number> = { ok: 0, degraded: 1, partial: 2, major: 3 }

/**
 * The parent's response narrowed to one product: its components, and a current state derived from
 * them rather than inherited.
 *
 * The uptime windows, the day strip and the incident list are deliberately left as the parent's,
 * exactly as `ProductPages.Narrow` leaves them. They are computed from incident spans, and an
 * incident's components are only published by some providers — claiming a product-specific
 * percentage this page cannot actually compute would be worse than showing the parent's and
 * saying whose it is, which is what the panel notes on the page do.
 */
export function narrowToProduct(product: Product, parent: ProviderStatus): ProviderStatus {
  const components = parent.components.filter((component) => matchesProduct(product, component))

  let worst: ComponentState = 'ok'
  for (const component of components) {
    if (SEVERITY[component.status] > SEVERITY[worst]) worst = component.status
  }
  const state: ProviderState =
    worst === 'ok' ? 'ok' : worst === 'degraded' ? 'degraded' : 'down'

  return {
    ...parent,
    name: product.label,
    components,
    current: { ...parent.current, state, down: state === 'down' },
  }
}

/** A link in the header menu and the footer's Status column. */
export interface StatusLink {
  readonly to: string
  /**
   * The phrase somebody types, not the bare brand: "ChatGPT status", never "ChatGPT"
   * (`docs/seo/keywords.md`). Anchor text is the strongest thing one page can say about another,
   * and a menu of company names says nothing about the query each page answers.
   */
  readonly label: string
}

/**
 * The seven status pages, each parent followed by its own products.
 *
 * `/status/gemini` is in the list even though its gate currently answers a redirect. It is a real
 * link for a real query, the redirect is one hop, and dropping it would mean the menu's contents
 * changed with Google's dashboard.
 */
export const STATUS_LINKS: readonly StatusLink[] = PROVIDERS.flatMap((provider) => [
  { to: `/status/${provider.id}`, label: statusPhrase(provider.id, `${provider.label} status`) },
  ...PRODUCTS.filter((product) => product.parent === provider.id).map((product) => ({
    to: `/status/${product.slug}`,
    label: statusPhrase(product.slug, `${product.label} status`),
  })),
])

/**
 * The breadcrumb trail for a status or product page, in the words `StatusPageComposer` uses.
 *
 * One model for both halves of the page and, later, for B28's `BreadcrumbList`: the server writes
 * this trail into the document a crawler reads, React draws it after the mount, and a third copy
 * in the JSON-LD is how the three would come to disagree.
 */
export function statusCrumbs(slug: string): readonly Crumb[] {
  const head: Crumb[] = [
    { label: 'Must Have AI', to: '/' },
    { label: 'Status', to: '/' },
  ]
  const product = findProduct(slug)
  if (product === null) return [...head, { label: pageTitle(slug) }]

  // The parent's plain company name, not its h1: the trail says where this page sits, and
  // "Google AI Status › Gemini Status" reads as two headings rather than as a path.
  const parent = PROVIDERS.find((candidate) => candidate.id === product.parent)
  return [
    ...head,
    { label: `${parent?.label ?? product.parent} Status`, to: `/status/${product.parent}` },
    { label: pageTitle(product.slug) },
  ]
}

/** A page's own h1, which is what its crumb is named after. */
function pageTitle(slug: string): string {
  return SEO_COPY[`status.${slug}`]?.h1 ?? slug
}

/**
 * "OpenAI Status" → "OpenAI status".
 *
 * Derived from the h1 rather than from the label beside it, because the h1 is the phrase the page
 * is written to win: Google's page is headed "Google AI Status", and a menu built from
 * `providers.ts` alone would link to it as "Google status" — a different query from the one the
 * page answers.
 *
 * Exported since F33: it is also the anchor text and the `alt` of the badge somebody pastes into a
 * README from this page, and a link in a stranger's repository that says "Must Have AI" instead of
 * "OpenAI status" is a link that says nothing about the page it points at.
 */
export function statusPhrase(slug: string, fallback: string): string {
  const h1 = SEO_COPY[`status.${slug}`]?.h1
  return h1 === undefined ? fallback : h1.replace(/ Status$/, ' status')
}
